import React, {FC, Fragment} from "react";
import {css} from "@panda/css";
import cn from "classnames";

import {Button} from "@pg-design/button-module";
import {mt} from "@pg-design/helpers-css";
import {BrandCalendarIcon} from "@pg-design/icons-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {OfferStatusStatus} from "@pg-rp/common-types/src/types/OfferStatusStatus";

import {useShouldShowPrice} from "../../hooks/use_should_show_price";
import {useLeadData} from "../../lead/hooks/use_lead_data";
import {AdditionalFormFieldType} from "../../lead/types/AdditionalFormFieldType";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {getHoursOptionsFromOfficesData} from "../../lead/utils/get_hours_options_from_offices_data";
import {getLeadButtonText} from "../../lead/utils/get_lead_button_text";
import {AddToFavouritesButton} from "../../my_offers/components/AddToFavouritesButton";
import {InquiryButtonTypeName} from "../../offer/detail/constants/inquiry_button_type";
import {PropertyFlatType} from "../../offer/detail/constants/PropertyFlatType";
import {IOfferSalesOffice} from "../../offer/types/IOfferSalesOffice";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {IPropertyDetail} from "../../property/types/IPropertyDetail";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {hotlineTelNumber} from "../../types/HotlineTelNumber";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {ApplicationVariant} from "../types/ApplicationVariant";
import {ApplicationBoxHeading} from "./ApplicationBoxHeading";
import {ApplicationDefaultRender} from "./ApplicationDefaultRender";
import {ApplicationHotlinePhoneNumber} from "./ApplicationHotlinePhoneNumber";
import {IApplicationModalButtonRenderProps} from "./ApplicationModalButton";
import {ApplicationModalButton} from "./ApplicationModalButton";
import {ApplicationPhoneNumber} from "./ApplicationPhoneNumber";
import {TermsAcceptation} from "./TermsAcceptation";

interface IProps {
    applicationVariant?: ApplicationVariant;
    source: number;
    sourceSection: number;
    storeHash: string;
    optionalVendorInfoData?: {
        name: string;
        logo: {
            v_log_120x90: string;
        } | null;
    };
    optionalOfferPhoneData?: IOfferPhoneData;
    offerStatusStatus?: OfferStatusStatus;
    isAutoresponderEnabled: boolean;
    sidebarBg?: "light" | "dark";
    vendorId: number;
    offerId?: number;
    property?: (IPropertyDetail | IPropertyListProperty) & {flat_type?: PropertyFlatType};
    forceClose?: boolean;
    disableVendorInfo?: boolean;
    formButtonText?: string;
    render?: (props: IApplicationModalButtonRenderProps) => React.JSX.Element;
    className?: string;
    footer?: React.ReactNode;
    officeData?: IOfferSalesOffice;
    modalState?: OfferModalActiveTab | null;
}

interface IOfferPhoneData {
    configuration: {
        phone_clip: string | null;
    };
    is_phone_clip_enabled: boolean;
}

export const ApplicationBox: FC<IProps> = (props) => {
    const {
        source,
        sourceSection,
        storeHash,
        optionalVendorInfoData,
        optionalOfferPhoneData,
        isAutoresponderEnabled,
        sidebarBg,
        vendorId,
        offerId,
        property,
        forceClose,
        className,
        modalState,
        offerStatusStatus
    } = props;

    const propertyId = property?.id;

    const leadData = useLeadData({
        storeHash,
        vendorId,
        offerId,
        propertyId
    });

    const isAuthenticated = useIsAuthenticated();
    const isDataFetched = Boolean(leadData);

    const vendor = optionalVendorInfoData ?? leadData?.vendor?.detail;
    const isOfferStatusToAcquire = offerStatusStatus === OfferStatusStatus.STATUS_STATUS_TO_ACQUIRE;
    const propertyPrice = leadData?.property?.detail?.price;
    const vendorName = vendor?.name ?? vendorId.toString();
    const offerName = leadData?.offer?.detail?.name ?? (offerId ? offerId.toString() : "");

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: leadData?.offer?.detail?.configuration.price_visibility,
        vendorPriceVisibility: leadData?.vendor?.detail?.configuration.price_visibility,
        vendorId
    });
    let defaultRenderSource;

    switch (modalState) {
        case OfferModalActiveTab.MAP:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_MAP;
            break;
        case OfferModalActiveTab.PLAN:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_FLOOR_PLAN;
            break;
        case OfferModalActiveTab.CONSTRUCTION_GALLERY:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_CONSTRUCTION_GALLERY;
            break;
        case OfferModalActiveTab.GALLERY:
        default:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_GALLERY;
    }

    const hoursOptions = getHoursOptionsFromOfficesData(props.officeData);
    const gtmDefaultRenderData = {
        clickText: getLeadButtonText({
            isPriceHidden: !shouldShowPrice,
            isProperty: !!propertyId,
            propertyPrice: propertyPrice
        }),
        source: modalState ? defaultRenderSource : GTMEventApplicationSourceSection.SIDEBAR_LEAD,
        vendorName,
        offerName
    };
    const gtmArrangeMeetingData = {
        clickText: InquiryButtonTypeName.ARRANGE_MEETING,
        source: modalState ? defaultRenderSource : GTMEventApplicationSourceSection.SIDEBAR_ARRANGE_MEETING,
        vendorName,
        offerName
    };

    const defaultRender = ({openModal, gtmData}: {openModal: (gtmProps: IGTMEventLeadForm) => void; gtmData: IGTMEventLeadForm}) => {
        return <ApplicationDefaultRender vendorId={vendorId} isProperty={!!propertyId} price={propertyPrice} openModal={openModal} gtmData={gtmData} />;
    };

    return (
        <Fragment>
            <div className={cn(cardWrapper, className)}>
                <div className={contactWrapper}>
                    <ApplicationBoxHeading
                        vendorName={vendor?.name}
                        vendorLogo={vendor?.logo && vendor.logo.v_log_120x90}
                        price={propertyPrice}
                        isDataFetched={isDataFetched}
                        shouldShowPrice={shouldShowPrice}
                        currency={leadData?.property?.detail?.currency}
                        offerType={property?.offer.type}
                        flatType={property?.flat_type}
                        isHolidayLocation={property?.offer.is_holiday_location}
                        isCondoHotel={property?.offer.is_condohotel}
                    />

                    <ApplicationModalButton
                        source={source}
                        sourceSection={sourceSection}
                        vendorId={vendorId}
                        offerId={offerId || null}
                        propertyId={propertyId || null}
                        forceClose={forceClose}
                        render={props.render || ((renderProps) => defaultRender({...renderProps, gtmData: gtmDefaultRenderData}))}
                        applicationVariant={props.applicationVariant}
                        currency={property?.offer.currency}
                    />

                    {isDataFetched && propertyPrice && (
                        <div className={meetingButton}>
                            <ApplicationModalButton
                                source={source}
                                sourceSection={sourceSection}
                                vendorId={vendorId}
                                offerId={offerId || null}
                                propertyId={propertyId || null}
                                forceClose={forceClose}
                                render={({openModal}) => (
                                    <Button
                                        variant="outlined_secondary"
                                        size="x-small"
                                        iconLeft={BrandCalendarIcon}
                                        onClick={() => openModal(gtmArrangeMeetingData)}
                                        style={{margin: "auto"}}
                                    >
                                        <Text as="span" variant="button_medium">
                                            Umów spotkanie
                                        </Text>
                                    </Button>
                                )}
                                applicationVariant={props.applicationVariant}
                                useAdditionalFormField={AdditionalFormFieldType.MEETING}
                                hourOptions={hoursOptions}
                                currency={property?.offer.currency}
                            />
                        </div>
                    )}

                    {isOfferStatusToAcquire ? (
                        <ApplicationHotlinePhoneNumber hotlineNumber={hotlineTelNumber} />
                    ) : (
                        <ApplicationPhoneNumber
                            showSeparatorText={Boolean(isDataFetched && propertyPrice)}
                            isAutoresponderEnabled={isAutoresponderEnabled}
                            isDataFetched={isDataFetched}
                            offer={leadData?.offer?.detail}
                            propertyId={propertyId}
                            propertyPrice={propertyPrice}
                            optionalOfferPhoneData={optionalOfferPhoneData}
                        />
                    )}

                    <TermsAcceptation css={mt(2)} sourceForm={{source: "applicationSideBar", isUserLogged: isAuthenticated}} />

                    <Text align="center" color={getThemeVariable("colors.gray.700")} variant="info_txt_2">
                        Administratorem Twoich danych osobowych <br /> jest Property Group sp. z o.o
                    </Text>
                </div>

                {props.footer}
            </div>
            {propertyId && offerId && (
                <div className={addToFavouritesButtonWrapStyle}>
                    <AddToFavouritesButton
                        className={addToFavouritesButtonStyle}
                        propertyId={propertyId}
                        offerId={offerId}
                        theme={sidebarBg ? sidebarBg : "light"}
                    />
                </div>
            )}
        </Fragment>
    );
};

const cardWrapper = css({
    width: "100%",
    backgroundColor: "white",
    height: "fit-content",
    borderRadius: 2,
    boxShadow: "0 16px 40px rgba(174,177,180,0.2)"
});

const contactWrapper = css({
    p: 3
});

const meetingButton = css({
    display: "flex",
    flexGrow: 0,
    alignItems: "center",
    marginTop: 2
});

const addToFavouritesButtonWrapStyle = css({
    display: "flex",
    justifyContent: "center",
    marginTop: 2
});

const addToFavouritesButtonStyle = css({
    //  We should place elements with margin-bottom, but since this element is dynamic, it needs to own the margin
    marginTop: 2
});
import React, {FC, Fragment} from "react";
import {css} from "@panda/css";
import cn from "classnames";

import {Button} from "@pg-design/button-module";
import {mt} from "@pg-design/helpers-css";
import {BrandCalendarIcon} from "@pg-design/icons-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {OfferStatusStatus} from "@pg-rp/common-types/src/types/OfferStatusStatus";

import {useShouldShowPrice} from "../../hooks/use_should_show_price";
import {useLeadData} from "../../lead/hooks/use_lead_data";
import {AdditionalFormFieldType} from "../../lead/types/AdditionalFormFieldType";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {getHoursOptionsFromOfficesData} from "../../lead/utils/get_hours_options_from_offices_data";
import {getLeadButtonText} from "../../lead/utils/get_lead_button_text";
import {AddToFavouritesButton} from "../../my_offers/components/AddToFavouritesButton";
import {InquiryButtonTypeName} from "../../offer/detail/constants/inquiry_button_type";
import {PropertyFlatType} from "../../offer/detail/constants/PropertyFlatType";
import {IOfferSalesOffice} from "../../offer/types/IOfferSalesOffice";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {IPropertyDetail} from "../../property/types/IPropertyDetail";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {hotlineTelNumber} from "../../types/HotlineTelNumber";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {ApplicationVariant} from "../types/ApplicationVariant";
import {ApplicationBoxHeading} from "./ApplicationBoxHeading";
import {ApplicationDefaultRender} from "./ApplicationDefaultRender";
import {ApplicationHotlinePhoneNumber} from "./ApplicationHotlinePhoneNumber";
import {IApplicationModalButtonRenderProps} from "./ApplicationModalButton";
import {ApplicationModalButton} from "./ApplicationModalButton";
import {ApplicationPhoneNumber} from "./ApplicationPhoneNumber";
import {TermsAcceptation} from "./TermsAcceptation";

interface IProps {
    applicationVariant?: ApplicationVariant;
    source: number;
    sourceSection: number;
    storeHash: string;
    optionalVendorInfoData?: {
        name: string;
        logo: {
            v_log_120x90: string;
        } | null;
    };
    optionalOfferPhoneData?: IOfferPhoneData;
    offerStatusStatus?: OfferStatusStatus;
    isAutoresponderEnabled: boolean;
    sidebarBg?: "light" | "dark";
    vendorId: number;
    offerId?: number;
    property?: (IPropertyDetail | IPropertyListProperty) & {flat_type?: PropertyFlatType};
    forceClose?: boolean;
    disableVendorInfo?: boolean;
    formButtonText?: string;
    render?: (props: IApplicationModalButtonRenderProps) => React.JSX.Element;
    className?: string;
    footer?: React.ReactNode;
    officeData?: IOfferSalesOffice;
    modalState?: OfferModalActiveTab | null;
}

interface IOfferPhoneData {
    configuration: {
        phone_clip: string | null;
    };
    is_phone_clip_enabled: boolean;
}

export const ApplicationBox: FC<IProps> = (props) => {
    const {
        source,
        sourceSection,
        storeHash,
        optionalVendorInfoData,
        optionalOfferPhoneData,
        isAutoresponderEnabled,
        sidebarBg,
        vendorId,
        offerId,
        property,
        forceClose,
        className,
        modalState,
        offerStatusStatus
    } = props;

    const propertyId = property?.id;

    const leadData = useLeadData({
        storeHash,
        vendorId,
        offerId,
        propertyId
    });

    const isAuthenticated = useIsAuthenticated();
    const isDataFetched = Boolean(leadData);

    const vendor = optionalVendorInfoData ?? leadData?.vendor?.detail;
    const isOfferStatusToAcquire = offerStatusStatus === OfferStatusStatus.STATUS_STATUS_TO_ACQUIRE;
    const propertyPrice = leadData?.property?.detail?.price;
    const vendorName = vendor?.name ?? vendorId.toString();
    const offerName = leadData?.offer?.detail?.name ?? (offerId ? offerId.toString() : "");

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: leadData?.offer?.detail?.configuration.price_visibility,
        vendorPriceVisibility: leadData?.vendor?.detail?.configuration.price_visibility,
        vendorId
    });
    let defaultRenderSource;

    switch (modalState) {
        case OfferModalActiveTab.MAP:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_MAP;
            break;
        case OfferModalActiveTab.PLAN:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_FLOOR_PLAN;
            break;
        case OfferModalActiveTab.CONSTRUCTION_GALLERY:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_CONSTRUCTION_GALLERY;
            break;
        case OfferModalActiveTab.GALLERY:
        default:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_GALLERY;
    }

    const hoursOptions = getHoursOptionsFromOfficesData(props.officeData);
    const gtmDefaultRenderData = {
        clickText: getLeadButtonText({
            isPriceHidden: !shouldShowPrice,
            isProperty: !!propertyId,
            propertyPrice: propertyPrice
        }),
        source: modalState ? defaultRenderSource : GTMEventApplicationSourceSection.SIDEBAR_LEAD,
        vendorName,
        offerName
    };
    const gtmArrangeMeetingData = {
        clickText: InquiryButtonTypeName.ARRANGE_MEETING,
        source: modalState ? defaultRenderSource : GTMEventApplicationSourceSection.SIDEBAR_ARRANGE_MEETING,
        vendorName,
        offerName
    };

    const defaultRender = ({openModal, gtmData}: {openModal: (gtmProps: IGTMEventLeadForm) => void; gtmData: IGTMEventLeadForm}) => {
        return <ApplicationDefaultRender vendorId={vendorId} isProperty={!!propertyId} price={propertyPrice} openModal={openModal} gtmData={gtmData} />;
    };

    return (
        <Fragment>
            <div className={cn(cardWrapper, className)}>
                <div className={contactWrapper}>
                    <ApplicationBoxHeading
                        vendorName={vendor?.name}
                        vendorLogo={vendor?.logo && vendor.logo.v_log_120x90}
                        price={propertyPrice}
                        isDataFetched={isDataFetched}
                        shouldShowPrice={shouldShowPrice}
                        currency={leadData?.property?.detail?.currency}
                        offerType={property?.offer.type}
                        flatType={property?.flat_type}
                        isHolidayLocation={property?.offer.is_holiday_location}
                        isCondoHotel={property?.offer.is_condohotel}
                    />

                    <ApplicationModalButton
                        source={source}
                        sourceSection={sourceSection}
                        vendorId={vendorId}
                        offerId={offerId || null}
                        propertyId={propertyId || null}
                        forceClose={forceClose}
                        render={props.render || ((renderProps) => defaultRender({...renderProps, gtmData: gtmDefaultRenderData}))}
                        applicationVariant={props.applicationVariant}
                        currency={property?.offer.currency}
                    />

                    {isDataFetched && propertyPrice && (
                        <div className={meetingButton}>
                            <ApplicationModalButton
                                source={source}
                                sourceSection={sourceSection}
                                vendorId={vendorId}
                                offerId={offerId || null}
                                propertyId={propertyId || null}
                                forceClose={forceClose}
                                render={({openModal}) => (
                                    <Button
                                        variant="outlined_secondary"
                                        size="x-small"
                                        iconLeft={BrandCalendarIcon}
                                        onClick={() => openModal(gtmArrangeMeetingData)}
                                        style={{margin: "auto"}}
                                    >
                                        <Text as="span" variant="button_medium">
                                            Umów spotkanie
                                        </Text>
                                    </Button>
                                )}
                                applicationVariant={props.applicationVariant}
                                useAdditionalFormField={AdditionalFormFieldType.MEETING}
                                hourOptions={hoursOptions}
                                currency={property?.offer.currency}
                            />
                        </div>
                    )}

                    {isOfferStatusToAcquire ? (
                        <ApplicationHotlinePhoneNumber hotlineNumber={hotlineTelNumber} />
                    ) : (
                        <ApplicationPhoneNumber
                            showSeparatorText={Boolean(isDataFetched && propertyPrice)}
                            isAutoresponderEnabled={isAutoresponderEnabled}
                            isDataFetched={isDataFetched}
                            offer={leadData?.offer?.detail}
                            propertyId={propertyId}
                            propertyPrice={propertyPrice}
                            optionalOfferPhoneData={optionalOfferPhoneData}
                        />
                    )}

                    <TermsAcceptation css={mt(2)} sourceForm={{source: "applicationSideBar", isUserLogged: isAuthenticated}} />

                    <Text align="center" color={getThemeVariable("colors.gray.700")} variant="info_txt_2">
                        Administratorem Twoich danych osobowych <br /> jest Property Group sp. z o.o
                    </Text>
                </div>

                {props.footer}
            </div>
            {propertyId && offerId && (
                <div className={addToFavouritesButtonWrapStyle}>
                    <AddToFavouritesButton
                        className={addToFavouritesButtonStyle}
                        propertyId={propertyId}
                        offerId={offerId}
                        theme={sidebarBg ? sidebarBg : "light"}
                    />
                </div>
            )}
        </Fragment>
    );
};

const cardWrapper = css({
    width: "100%",
    backgroundColor: "white",
    height: "fit-content",
    borderRadius: 2,
    boxShadow: "0 16px 40px rgba(174,177,180,0.2)"
});

const contactWrapper = css({
    p: 3
});

const meetingButton = css({
    display: "flex",
    flexGrow: 0,
    alignItems: "center",
    marginTop: 2
});

const addToFavouritesButtonWrapStyle = css({
    display: "flex",
    justifyContent: "center",
    marginTop: 2
});

const addToFavouritesButtonStyle = css({
    //  We should place elements with margin-bottom, but since this element is dynamic, it needs to own the margin
    marginTop: 2
});
